import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Building, 
  Shield, 
  Calendar, 
  Edit3, 
  Save, 
  X, 
  Hash, 
  Globe,
  Crown,
  LogOut,
  Lock,
  Eye,
  EyeOff,
  Key
} from 'lucide-react';

const ProfilePage = () => {
  const { user, logout, updateProfile, changePassword } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Password change states
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Initialize edit data when user data changes
  React.useEffect(() => {
    if (user) {
      setEditData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        postal_code: user.postal_code || '',
        country: user.country || ''
      });
    }
  }, [user]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      address: user.address || '',
      city: user.city || '',
      postal_code: user.postal_code || '',
      country: user.country || ''
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const result = await updateProfile(editData);
      if (result.success) {
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Update error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Password change functions
  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (passwordError) setPasswordError('');
    if (passwordSuccess) setPasswordSuccess('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handlePasswordSubmit = async () => {
    // Clear previous messages
    setPasswordError('');
    setPasswordSuccess('');

    // Validation étape par étape
    if (!passwordData.currentPassword) {
      setPasswordError('Veuillez saisir votre mot de passe actuel');
      return;
    }

    if (!passwordData.newPassword) {
      setPasswordError('Veuillez saisir le nouveau mot de passe');
      return;
    }

    if (!passwordData.confirmPassword) {
      setPasswordError('Veuillez confirmer le nouveau mot de passe');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Les nouveaux mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordError('Le nouveau mot de passe doit être différent de l\'ancien');
      return;
    }

    setPasswordLoading(true);

    try {
      // Utiliser la vraie fonction changePassword de l'AuthContext
      const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
      
      if (result.success) {
        setPasswordSuccess('Mot de passe modifié avec succès !');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setIsChangingPassword(false);
        
        // Clear success message after 5 seconds
        setTimeout(() => setPasswordSuccess(''), 5000);
      } else {
        setPasswordError(result.error || 'Erreur lors du changement de mot de passe');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordError('Erreur lors du changement de mot de passe. Veuillez réessayer.');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Validation en temps réel pour la confirmation du mot de passe
  const validatePasswordMatch = () => {
    if (passwordData.newPassword && passwordData.confirmPassword) {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        return 'Les mots de passe ne correspondent pas';
      }
      if (passwordData.newPassword.length < 6) {
        return 'Le mot de passe doit contenir au moins 6 caractères';
      }
    }
    return null;
  };

  const getPasswordMatchError = () => {
    return validatePasswordMatch();
  };

  const cancelPasswordChange = () => {
    setIsChangingPassword(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-iranian-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-iranian-400 to-iranian-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <User className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Utilisateur non connecté</h1>
          <p className="text-gray-300">Veuillez vous connecter pour voir votre profil.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-iranian-900 py-8 mt-16">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="relative">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-r from-iranian-500/20 to-yellow-500/20 blur-3xl rounded-full"></div>
            
            {/* Profile avatar */}
            <div className="relative w-32 h-32 bg-gradient-to-br from-iranian-400 via-iranian-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-iranian-500/25">
              <Crown className="h-16 w-16 text-white" />
            </div>
          </div>
          
          <h1 className="text-5xl font-bold text-white mb-4 bg-gradient-to-r from-white to-iranian-200 bg-clip-text text-transparent">
            {user.first_name} {user.last_name}
          </h1>
          
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Bienvenue sur votre espace personnel. Gérez vos informations et suivez vos commandes.
          </p>
          
          {/* Action Buttons */}
          <div className="flex gap-4 justify-center">
            {!isEditing ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleEdit}
                className="bg-gradient-to-r from-iranian-500 to-iranian-600 hover:from-iranian-600 hover:to-iranian-700 text-white px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-2xl shadow-iranian-500/25 hover:shadow-iranian-500/40 flex items-center gap-3"
              >
                <Edit3 className="h-5 w-5" />
                Modifier le profil
              </motion.button>
            ) : (
              <>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-2xl hover:shadow-green-500/40 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-5 w-5" />
                  {loading ? 'Sauvegarde...' : 'Sauvegarder'}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleCancel}
                  className="bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-2xl hover:shadow-gray-500/40 flex items-center gap-3"
                >
                  <X className="h-5 w-5" />
                  Annuler
                </motion.button>
              </>
            )}
          </div>
        </motion.div>
        
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 mb-12">
          
          {/* Personal Information Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-gray-700/50 p-8 hover:border-iranian-500/30 transition-all duration-500 group min-w-[480px]"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-iranian-400 to-iranian-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">Informations Personnelles</h2>
            </div>
            
            <div className="space-y-6">
              {/* First Name */}
              <div className="group/item">
                <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center gap-3">
                  <User className="h-4 w-4 text-iranian-400" />
                  Prénom
                </label>
                <div className="relative">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      className="w-full bg-gray-700/70 border-2 border-iranian-500/50 rounded-xl px-5 py-4 text-white text-lg focus:outline-none focus:ring-2 focus:ring-iranian-500/50 focus:border-iranian-500 transition-all duration-300 hover:border-iranian-500/70 placeholder-gray-400"
                      placeholder="Votre prénom"
                    />
                  ) : (
                    <div className="text-white text-lg font-medium py-2">
                      {user.first_name}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Last Name */}
              <div className="group/item">
                <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center gap-3">
                  <User className="h-4 w-4 text-iranian-400" />
                  Nom
                </label>
                <div className="relative">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      className="w-full bg-gray-700/70 border-2 border-iranian-500/50 rounded-xl px-5 py-4 text-white text-lg focus:outline-none focus:ring-2 focus:ring-iranian-500/50 focus:border-iranian-500 transition-all duration-300 hover:border-iranian-500/70 placeholder-gray-400"
                      placeholder="Votre nom"
                    />
                  ) : (
                    <div className="text-white text-lg font-medium py-2">
                      {user.last_name}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Email */}
              <div className="group/item">
                <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center gap-3">
                  <Mail className="h-4 w-4 text-iranian-400" />
                  Adresse email
                </label>
                <div className="text-white text-lg font-medium py-2 break-all">
                  {user.email}
                </div>
              </div>
              
              {/* Phone */}
              <div className="group/item">
                <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center gap-3">
                  <Phone className="h-4 w-4 text-iranian-400" />
                  Téléphone
                </label>
                <div className="relative">
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full bg-gray-700/70 border-2 border-iranian-500/50 rounded-xl px-5 py-4 text-white text-lg focus:outline-none focus:ring-2 focus:ring-iranian-500/50 focus:border-iranian-500 transition-all duration-300 hover:border-iranian-500/70 placeholder-gray-400"
                      placeholder="Votre numéro de téléphone"
                    />
                  ) : (
                    <div className="text-white text-lg font-medium py-2">
                      {user.phone || 'Non renseigné'}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Member Since */}
              <div className="group/item">
                <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-iranian-400" />
                  Membre depuis
                </label>
                <div className="text-white text-lg font-medium py-2">
                  {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'Récemment'}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Address Information Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-gray-700/50 p-8 hover:border-iranian-500/30 transition-all duration-500 group min-w-[480px]"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-2xl flex items-center justify-center shadow-lg">
                <MapPin className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">Adresse</h2>
            </div>
            
            <div className="space-y-6">
              {/* Address */}
              <div className="group/item">
                <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-yellow-400" />
                  Adresse complète
                </label>
                <div className="relative">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className="w-full bg-gray-700/70 border-2 border-yellow-500/50 rounded-xl px-5 py-4 text-white text-lg focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all duration-300 hover:border-yellow-500/70 placeholder-gray-400"
                      placeholder="Votre adresse complète"
                    />
                  ) : (
                    <div className="text-white text-lg font-medium py-2 break-words">
                      {user.address || 'Non renseignée'}
                    </div>
                  )}
                </div>
              </div>
              
              {/* City */}
              <div className="group/item">
                <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center gap-3">
                  <Building className="h-4 w-4 text-yellow-400" />
                  Ville
                </label>
                <div className="relative">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="w-full bg-gray-700/70 border-2 border-yellow-500/50 rounded-xl px-5 py-4 text-white text-lg focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all duration-300 hover:border-yellow-500/70 placeholder-gray-400"
                      placeholder="Votre ville"
                    />
                  ) : (
                    <div className="text-white text-lg font-medium py-2">
                      {user.city || 'Non renseignée'}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Postal Code */}
              <div className="group/item">
                <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center gap-3">
                  <Hash className="h-4 w-4 text-yellow-400" />
                  Code postal
                </label>
                <div className="relative">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.postal_code}
                      onChange={(e) => handleInputChange('postal_code', e.target.value)}
                      className="w-full bg-gray-700/70 border-2 border-yellow-500/50 rounded-xl px-5 py-4 text-white text-lg focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all duration-300 hover:border-yellow-500/70 placeholder-gray-400"
                      placeholder="Code postal"
                    />
                  ) : (
                    <div className="text-white text-lg font-medium py-2">
                      {user.postal_code || 'Non renseigné'}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Country */}
              <div className="group/item">
                <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center gap-3">
                  <Globe className="h-4 w-4 text-yellow-400" />
                  Pays
                </label>
                <div className="relative">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      className="w-full bg-gray-700/70 border-2 border-yellow-500/50 rounded-xl px-5 py-4 text-white text-lg focus:outline-none focus:border-yellow-500 transition-all duration-300 hover:border-yellow-500/70 placeholder-gray-400"
                      placeholder="Votre pays"
                    />
                  ) : (
                    <div className="text-white text-lg font-medium py-2">
                      {user.country || 'Non renseigné'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Password Change Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-gray-700/50 p-8 hover:border-purple-500/30 transition-all duration-500 group min-w-[480px]"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Key className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">Sécurité</h2>
            </div>

            {!isChangingPassword ? (
              <div className="space-y-6">
                <p className="text-gray-300 text-center">
                  Changez votre mot de passe pour sécuriser votre compte
                </p>
                
                <div className="pt-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsChangingPassword(true)}
                    className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-6 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-2xl hover:shadow-purple-500/40 flex items-center justify-center gap-3"
                  >
                    <Lock className="h-5 w-5" />
                    Changer le mot de passe
                  </motion.button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white mb-6 text-center">
                  Modifier le mot de passe
                </h3>

                {/* Current Password */}
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center gap-3">
                    <Lock className="h-4 w-4 text-purple-400" />
                    Mot de passe actuel
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.current ? "text" : "password"}
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                      className="w-full bg-gray-700/70 border-2 border-purple-500/50 rounded-xl px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all duration-300 hover:border-purple-500/70 placeholder-gray-400 pr-12"
                      placeholder="Mot de passe actuel"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('current')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center gap-3">
                    <Key className="h-4 w-4 text-purple-400" />
                    Nouveau mot de passe
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.new ? "text" : "password"}
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                      className={`w-full bg-gray-700/70 border-2 rounded-xl px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all duration-300 placeholder-gray-400 pr-12 ${
                        passwordData.newPassword && passwordData.newPassword.length < 6 
                          ? 'border-red-500/50 focus:border-red-500' 
                          : 'border-purple-500/50 focus:border-purple-500 hover:border-purple-500/70'
                      }`}
                      placeholder="Min. 6 caractères"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {passwordData.newPassword && passwordData.newPassword.length < 6 && (
                    <p className="text-red-400 text-sm mt-2">Le mot de passe doit contenir au moins 6 caractères</p>
                  )}
                </div>

                {/* Confirm New Password */}
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center gap-3">
                    <Key className="h-4 w-4 text-purple-400" />
                    Confirmer
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirm ? "text" : "password"}
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                      className={`w-full bg-gray-700/70 border-2 rounded-xl px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all duration-300 placeholder-gray-400 pr-12 ${
                        passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword 
                          ? 'border-red-500/50 focus:border-red-500' 
                          : 'border-purple-500/50 focus:border-purple-500 hover:border-purple-500/70'
                      }`}
                      placeholder="Confirmer le mot de passe"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                    <p className="text-red-400 text-sm mt-2">Les mots de passe ne correspondent pas</p>
                  )}
                </div>

                {/* Error and Success Messages */}
                {passwordError && (
                  <div className="bg-gradient-to-r from-red-500/20 to-rose-500/20 border-2 border-red-500/50 rounded-xl px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-red-400 to-rose-500 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                      <span className="text-red-300 font-semibold text-lg">Attention !</span>
                    </div>
                    <p className="text-red-200 text-base font-medium">
                      {passwordError}
                    </p>
                  </div>
                )}

                {passwordSuccess && (
                  <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500/50 rounded-xl px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-green-300 font-semibold text-lg">Succès !</span>
                    </div>
                    <p className="text-green-200 text-base font-medium">
                      Votre mot de passe a été modifié avec succès !
                    </p>
                    <p className="text-green-300/80 text-sm mt-2">
                      Vous pouvez maintenant utiliser votre nouveau mot de passe pour vos prochaines connexions.
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handlePasswordSubmit}
                    disabled={passwordLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword || passwordData.newPassword.length < 6 || passwordData.newPassword !== passwordData.confirmPassword}
                    className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-purple-500/40 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    <Save className="h-4 w-4" />
                    {passwordLoading ? 'Modification...' : 'Modifier'}
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={cancelPasswordChange}
                    className="flex-1 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-gray-500/40 flex items-center justify-center gap-2 text-sm"
                  >
                    <X className="h-4 w-4" />
                    Annuler
                  </motion.button>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Logout Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={logout}
            className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-10 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-2xl hover:shadow-red-500/40 flex items-center gap-3 mx-auto"
          >
            <LogOut className="h-5 w-5" />
            Se déconnecter
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage; 